/**
 * Aturan murni Pendataan Awal v7.50 (tanpa akses tabel) — dipakai handler `pendataanAwal.handler.ts`, seed, dan uji.
 * Sumber: prototype `assets/jal-pendataan.js` (`P.STD`, `P.kategori`, `P.usulLabel`, alasan) dan `assets/jal-impor.js`
 * (`JalImpor.angka`), dengan keputusan Q-11: saldo selalu bilangan bulat (desimal ditolak, tidak dibulatkan).
 */

const fmtId = (value: number) => new Intl.NumberFormat('id-ID').format(value)

export const PD_UNIT_STATUS = {
  awaiting: 'AWAITING_FIRST_OPNAME',
  cancelled: 'CANCELLED_OPENING',
  lost: 'LOST_FIRST_OPNAME'
} as const

export type PdCategory =
  | 'Etiket'
  | 'CTP'
  | 'Foil'
  | 'OPP'
  | 'Box'
  | 'Inner'
  | 'Dospress'
  | 'Lem'
  | 'Filter'
  | 'Papir/Bobin'
  | 'Lain-lain'

/** Ukuran standar per kategori (prototype `P.STD`): [kemasan, isi per kemasan, satuan isi] — hanya usulan. */
export const PD_STD: Record<PdCategory, readonly [string, number, string]> = {
  Etiket: ['Box', 5000, 'lembar'],
  CTP: ['Pak', 50, 'lembar'],
  Foil: ['Dus', 10, 'roll'],
  OPP: ['Dus', 6, 'roll'],
  Box: ['Ikat', 50, 'pcs'],
  Inner: ['Dus', 10, 'roll'],
  Dospress: ['Ikat', 100, 'pcs'],
  Lem: ['Jerigen', 20, 'kg'],
  Filter: ['Dos', 6, 'pcs'],
  'Papir/Bobin': ['Roll', 1, 'roll'],
  'Lain-lain': ['Dus', 10, 'pcs']
}
export const PD_CATEGORIES = Object.keys(PD_STD) as PdCategory[]
export const PD_SALDO_REASONS = [
  'Salah ketik di Excel',
  'Hitung ulang gudang',
  'Barang sudah keluar belum tercatat',
  'Lainnya'
] as const
export const PD_LOST_REASONS = [
  'Tidak ditemukan di gudang',
  'Rusak atau tidak layak',
  'Sudah terpakai belum tercatat'
] as const
export const PD_REPRINT_REASONS = ['Stiker rusak', 'Stiker hilang', 'Salah cetak'] as const

/** Kategori master → kategori Pendataan Awal (prototype `DARI_MASTER`) dan sebaliknya (`KAT_MASTER`). */
export const PD_FROM_MASTER: Record<string, PdCategory> = {
  papir: 'Papir/Bobin',
  tembakau: 'Lain-lain',
  teartape: 'Lain-lain',
  kraft: 'Lain-lain'
}
export const PD_TO_MASTER: Partial<Record<PdCategory, string>> = { 'Papir/Bobin': 'Papir' }

/** Kategori dari kata pertama nama material (prototype `P.kategori`). */
export function pdCategoryOf(name: unknown): PdCategory {
  const word =
    String(name ?? '')
      .toLowerCase()
      .replace(/[^a-z]/g, ' ')
      .trim()
      .split(/\s+/)[0] ?? ''
  if (/^etike/.test(word)) return 'Etiket'
  if (word === 'ctp') return 'CTP'
  if (word === 'foil') return 'Foil'
  if (word === 'opp') return 'OPP'
  if (word === 'box') return 'Box'
  if (word === 'inner') return 'Inner'
  if (/^dos/.test(word)) return 'Dospress'
  if (word === 'lem') return 'Lem'
  if (word === 'filter') return 'Filter'
  if (/^(bobin|bobbin|papir|papier)/.test(word)) return 'Papir/Bobin'
  return 'Lain-lain'
}

/** Huruf-angka saja, huruf kecil — pencocokan nama Excel dengan Master Material. */
export const normName = (value: unknown) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')

export type SaldoParse = { kind: 'ok' | 'zero' | 'invalid' | 'decimal'; value: number | null }

/**
 * Saldo dari sel Excel (prototype `JalImpor.angka`): angka; teks "1.234" / "1,234" = ribuan; "1.234,5" atau "1,5" =
 * desimal. Saldo SELALU bilangan bulat (keputusan Q-11): desimal ditolak, tidak dibulatkan.
 */
export function parseSaldo(raw: unknown): SaldoParse {
  let value: number
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return { kind: 'invalid', value: null }
    value = raw
  } else {
    const trimmed = String(raw ?? '').trim()
    if (!trimmed || trimmed === '-' || trimmed === '—') return { kind: 'zero', value: 0 }
    let text = trimmed.replace(/\s+/g, '').replace(/[^\d.,-]/g, '')
    if (!/\d/.test(text)) return { kind: 'invalid', value: null }
    const comma = text.lastIndexOf(',')
    const dot = text.lastIndexOf('.')
    if (comma >= 0 && dot >= 0) text = comma > dot ? text.replace(/\./g, '').replace(',', '.') : text.replace(/,/g, '')
    else if (comma >= 0) text = /^-?\d{1,3}(,\d{3})+$/.test(text) ? text.replace(/,/g, '') : text.replace(',', '.')
    else if (dot >= 0) text = /^-?\d{1,3}(\.\d{3})+$/.test(text) ? text.replace(/\./g, '') : text
    value = Number.parseFloat(text)
    if (Number.isNaN(value)) return { kind: 'invalid', value: null }
  }
  if (value <= 0) return { kind: 'zero', value: 0 }
  if (!Number.isInteger(value)) return { kind: 'decimal', value }
  return { kind: 'ok', value }
}

export type PackPart = { n: number; isi: number }

/** Usulan label (prototype `P.usulLabel`): kemasan penuh + satu kemasan parsial bila ada sisa. */
export function labelProposal(saldo: number, isi: number, packName = 'kemasan', smallName = '') {
  if (!(isi > 0) || !(saldo > 0)) return null
  const full = Math.floor(saldo / isi)
  const rest = saldo % isi
  const count = full + (rest ? 1 : 0)
  const parts: string[] = []
  if (full) parts.push(`${fmtId(full)} ${packName.toLowerCase()} penuh`)
  if (rest) parts.push(`1 parsial (${fmtId(rest)}${smallName ? ` ${smallName}` : ''})`)
  const plan: PackPart[] = [...(full ? [{ n: full, isi }] : []), ...(rest ? [{ n: 1, isi: rest }] : [])]
  return { text: `${parts.join(' + ')} = ${fmtId(count)} label`, count, full, rest, plan }
}
